import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import '../styles/DashboardFixes.css';
import AuthService from '../services/auth.service';
import ComplaintService from '../services/complaint.service';
import NewComplaintForm from '../components/NewComplaintForm';
import ComplaintList from '../components/ComplaintList';
import AdminHeader from '../components/AdminHeader';
import Footer from '../components/Footer';

const Dashboard: React.FC = () => {  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [activeView, setActiveView] = useState<'home' | 'newRequest' | 'myRequests' | 'viewDetail'>('home');
  const [refreshList, setRefreshList] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState<string | null>(null);
  const [complaintDetail, setComplaintDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Set document title
  useEffect(() => {
    document.title = 'Dashboard - Ruhuna Engineering Faculty MRS';
  }, []);
  const handleLogout = () => {
    AuthService.logout();
    navigate('/login');
  };

  const handleFormSuccess = () => {
    // Show success message and reset view to myRequests
    alert('Your maintenance request has been submitted successfully!');
    setRefreshList(!refreshList); // Trigger refresh of complaints list
    setActiveView('myRequests');
  };
  const handleViewDetail = async (id: string) => {
    setSelectedComplaintId(id);
    setDetailLoading(true);
    try {
      const detail = await ComplaintService.getComplaintDetails(id);
      setComplaintDetail(detail);
      setActiveView('viewDetail');
    } catch (error) {
      alert('Failed to load complaint details. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRefreshDetail = async () => {
    if (!selectedComplaintId) return;
    
    setDetailLoading(true);
    try {
      const detail = await ComplaintService.getComplaintDetails(selectedComplaintId);
      setComplaintDetail(detail);
    } catch (error) {
      alert('Failed to refresh complaint details. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  };
  const renderContent = () => {
    switch (activeView) {
      case 'newRequest':
        return (
          <NewComplaintForm 
            onSuccess={handleFormSuccess} 
            onCancel={() => setActiveView('home')} 
          />
        );
      case 'myRequests':
        return <ComplaintList onViewDetail={handleViewDetail} />;
      case 'viewDetail':
        return (
          detailLoading ? (
            <div className="text-center p-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : complaintDetail ? (
            <div className="complaint-detail">
              <h3>{complaintDetail.subject}</h3>
              <div className="mb-3">
                <span className={`badge ${complaintDetail.status === 'Pending' ? 'bg-warning text-dark' : 
                  complaintDetail.status === 'Ongoing' ? 'bg-primary' : 'bg-success'}`}>
                  {complaintDetail.status}
                </span>
                <span className="ms-2 text-muted">Category: {complaintDetail.category}</span>
              </div>
              
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title">Description</h5>
                  <p className="card-text">{complaintDetail.description}</p>
                </div>
              </div>
              
              {complaintDetail.photo_url && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h5 className="card-title">Photo</h5>
                    <img 
                      src={`${process.env.REACT_APP_BASE_URL || 'http://10.50.227.120:3000'}${complaintDetail.photo_url}`} 
                      alt="Complaint attachment" 
                      className="img-fluid rounded"
                      style={{ maxHeight: '300px' }}
                    />
                  </div>
                </div>
              )}
                {complaintDetail.status_updates && complaintDetail.status_updates.length > 0 && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h5 className="card-title">Status Updates</h5>
                    <ul className="list-group">
                      {complaintDetail.status_updates.map((update: any) => (
                        <li className="list-group-item" key={update.update_id}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className={`badge ${
                              update.updated_status === 'Pending' ? 'bg-warning text-dark' : 
                              update.updated_status === 'Ongoing' ? 'bg-primary' : 'bg-success'
                            }`}>
                              {update.updated_status}
                            </span>
                            <small className="text-muted">
                              Updated by: {update.first_name} {update.last_name}
                            </small>
                          </div>
                          <p className="mb-1">{update.update_text}</p>
                          <small className="text-muted">
                            {new Date(update.created_at).toLocaleDateString('en-US', {
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {complaintDetail.activities && complaintDetail.activities.length > 0 && (
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">Activities</h5>
                    <ul className="list-group">
                      {complaintDetail.activities.map((activity: any) => (
                        <li className="list-group-item" key={activity.activity_id}>
                          <p className="mb-1">{activity.description}</p>
                          <small className="text-muted">
                            {new Date(activity.created_at).toLocaleDateString('en-US', {
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
                <button 
                className="btn btn-secondary mt-3 me-2"
                onClick={() => setActiveView('myRequests')}
              >
                Back to My Requests
              </button>
              <button 
                className="btn btn-primary mt-3"
                onClick={handleRefreshDetail}
                disabled={detailLoading}
              >
                {detailLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Refreshing...
                  </>
                ) : 'Refresh Details'}
              </button>
            </div>
          ) : (
            <div className="alert alert-danger">
              Failed to load complaint details. Please try again.
            </div>
          )
        );
      default:
        return (
          <>
            <h5 className="card-title">Welcome to the Maintenance Management System</h5>
            <p className="card-text">
              You have successfully logged into the Maintenance Management System.
            </p>
            <p>From here, you can submit maintenance requests, track their status, and communicate with maintenance staff.</p>
            
            <div className="dashboard-actions mt-4">
              <button 
                className="btn btn-primary me-2"
                onClick={() => setActiveView('newRequest')}
              >
                New Request
              </button>
              <button 
                className="btn btn-info me-2"
                onClick={() => setActiveView('myRequests')}
              >
                My Requests
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveView('myRequests')}
              >
                View Status
              </button>
            </div>
          </>
        );
    }
  };

  return (
    <div className="dashboard-container">
      <AdminHeader user={user}  />

      <div className="content-container">
        <div className="container mt-4">
          <div className="row">
            <div className="col-md-12">
              <div className="card">                <div className="card-header d-flex justify-content-between align-items-center">
                  <h3>{activeView === 'home' ? 'Dashboard' : 
                      activeView === 'newRequest' ? 'New Maintenance Request' : 
                      activeView === 'viewDetail' ? 'Request Details' :
                      'My Maintenance Requests'}</h3>
                  {activeView !== 'home' && (
                    <button 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setActiveView('home')}
                    >
                      Back to Dashboard
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;